/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { zWithTagsFactory } from '@socios/configuration-cli';
import { z } from 'zod';

import { EnabledLocaleSchema } from '../../localization/locale/EnabledLocaleSchema';
import { TabNavigationItemName } from '../../models/navigation';
import ZodBuilderUtils from '../utils/ZodBuilderUtils';

import { AppStoreReviewConfigSchema, ExchangeSchema, PwaForceInstallSchema } from './schemas';
import { TagSchema } from './schemas/TagSchema';

const zWithTags = zWithTagsFactory(TagSchema);

export const SettingsSchema = z
	.object({
		accountVerificationLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/sections/360005704877-Account-Verification')
			.describe('Account verification link'),
		allowDecimalTokenTransaction: z.boolean().default(false).describe('Allow decimal token transaction'),
		androidVersion: z.string().default('0.0.0').describe('Android version'),
		appStoreReviewConfig: AppStoreReviewConfigSchema.describe('App Store review flow configuration'),
		appStoreUrlCapsicum: z.string().default('https://itunes.apple.com/app/id1464868277').describe('App store url'),
		areRequirementsAnalyticsEnabled: z
			.boolean()
			.default(false)
			.describe('Are requirements analytics track enabled'),
		areTutorialsEnabled: z
			.object({
				auctionDetails: z.boolean(),
				auctionsLobby: z.boolean(),
				partnerPage: z.boolean(),
				rewardsCategory: z.boolean(),
				rewardsLobby: z.boolean(),
				walletOnboarding: z.boolean()
			})
			.default({
				auctionDetails: false,
				auctionsLobby: false,
				partnerPage: false,
				rewardsCategory: false,
				rewardsLobby: false,
				walletOnboarding: false
			})
			.describe('Enable/Disable tutorials by feature'),
		auctionRefreshInterval: z.number().default(30).describe('Auction refresh interval'),
		bankOfNegaraLink: z
			.string()
			.default('https://www.bnm.gov.my/fep#notices')
			.describe('Link to documentation on Bank of Negara '),
		cc2ExplorerLink: z.string().default('https://scan.chiliz.com/').describe('CC2 explorer link'),
		chilizAndSociosInsiderTradingPolicyLink: z
			.string()
			.default('https://www.socios.com/insider-trading-policy/')
			.describe('Chiliz and Socios Insider Trading Policy Link'),
		chilizChainDocumentationLink: z
			.string()
			.default('https://governance.chilizchain.com/')
			.describe('Chiliz Chain documentation link'),
		chilizUrl: z.string().default('https://www.chiliz.com/').describe('Chiliz url'),
		chzAcquisitionCampaignConfig: ZodBuilderUtils.fromJsonStringSchema(
			z.object({
				dateEnd: z.string(),
				dateStart: z.string(),
				kycDelay: z.string(),
				vatCountries: z.array(z.string())
			}),
			{
				dateEnd: '',
				dateStart: '',
				kycDelay: '',
				vatCountries: []
			}
		).describe('Acquisition campaign config'),
		comeJoinUsLink: z.string().default('https://www.chiliz.com/en/careers/').describe('Come join us link'),
		contactUsHereLink: z
			.string()
			.default('https://mobile-app-content.socios.com/en_gb/contact-us/')
			.describe('Contact us link'),
		contactUsHereLinkForUsaCapsicum: z
			.string()
			.default('https://socios-us.zendesk.com/hc/en-us')
			.describe('Contact us link for USA'),
		cookiesPolicyLink: z.string().default('https://www.socios.com/cookies-policy/').describe('Cookies policy link'),
		dAppsTermsLink: z
			.string()
			.default('https://mobile-app-content.socios.com/en_gb/terms-and-conditions-socios-com-dapps/')
			.describe('dApps terms link'),
		deepLinkAllowedExternalUrlPatterns: z
			.array(z.string())
			.default([])
			.describe('Allowed URL patterns for deep links. ex: ^https://www\\.socios\\.com/.*$'),
		defaultCapsicumAvatarUrl: z
			.string()
			.default('https://assets.chiliz.com/images/user/default-avatar/{chilizUserId}.jpg')
			.describe('Default avatar url'),
		dfnsAndroidOrigin: z
			.string()
			.default('')
			.describe(
				'The Origin used for DFNS wallet creation & signing for Android users.' +
					' The is only used in local testing and builds for the integration environment.'
			),
		dfnsIosOrigin: z
			.string()
			.default('')
			.describe(
				'The Origin used for DFNS wallet creation & signing for iOS users.' +
					' This is only used in local testing and builds for the integration environment.'
			),
		enabledBehaviorsToasts: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Enabled behaviors toasts'
		),
		enabledLocales: ZodBuilderUtils.fromJsonStringSchema(z.array(EnabledLocaleSchema), []).describe(
			'Enabled locales'
		),
		enabledUsaFanTokenWithdrawal: z.boolean().default(false).describe('Enable USA fan token withdrawal'),
		enablePartnerFiltersV2: z.boolean().default(true).describe('Enable V2 of partner filters'),
		exchangesNetworkCompatibility: z
			.object({
				exchanges: z.array(ExchangeSchema)
			})
			.default({ exchanges: [] })
			.describe('Exchanges network compatibilitie'),
		facebookLink: z.string().default('https://www.facebook.com/sociosdotcom/').describe('Facebook link'),
		fanRewardsLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/categories/18107028562961-Fan-Rewards')
			.describe('Fan rewards link'),
		fanTokenComLink: z.string().default('https://www.fantokens.com/').describe('Fan token webside link'),
		fanTokenDirectPurchaseProcessExpirationPeriod: z
			.string()
			.default('PT15M')
			.describe('Period in mins that user have to finish the FanToken Direct Purchase journey'),
		fanTokenOfferingTermsLink: z
			.string()
			.default('https://mobile-app-content.socios.com/en_gb/fan-token-offering-terms/')
			.describe('Fan token offering terms link'),
		fanTokenOfferingTermsLinkUK: z
			.string()
			.default('https://www.socios.com/uk/learn-more/')
			.describe('Fan token offering terms link UK'),
		fanTokenStakingEnabled: z
			.object({
				claim: z.boolean(),
				stake: z.boolean(),
				unstake: z.boolean()
			})
			.default({ claim: false, stake: false, unstake: false })
			.describe('Enables entry points to FanTokenStaking features: stake, unstake and claim'),
		fanTokenStakingTermsAndConditionsLink: z
			.string()
			.default('https://mobile-app-content.socios.com/en_gb/terms-conditions-stake-earn/')
			.describe('Fan Token Staking Terms and Conditions link'),
		fanTokenV2MigrationEnabled: z
			.boolean()
			.default(false)
			.describe('Determines whether v2 FT Migration is enabled or not'),
		faqsLink: z.string().default('https://socios.zendesk.com/hc/fr').describe('FAQs link'),
		featureYourAppUrlPoblano: z.string().default('').describe('Feature your app'),
		feedCachePersistenceDuration: z.number().default(120000).describe('Feed cache persistence duration'),
		'feedCachePersistenceDuration_2.30.2': z
			.number()
			.default(180000)
			.describe('Feed cache persistence duration (for v2.30.2)'),
		feedPullToRefreshAvailAttempts: z.number().default(3).describe('Feed pull to refresh available attempts'),
		fingerprintJSConfig: z
			.object({
				allowUseOfLocationData: z.boolean().optional(),
				apiKey: z.string(),
				enabled: z.boolean(),
				endpoint: z.string().optional(),
				integrationInfo: z.array(z.string()).optional(),
				locationTimeoutMillisAndroid: z.number().optional(),
				region: z.enum(['eu', 'ap', 'us']).optional(),
				remoteControlDetection: z.boolean().optional(),
				scriptUrlPattern: z.string().optional()
			})
			// update defaults before merging
			.default({
				allowUseOfLocationData: true,
				apiKey: 'vci6f7bIaROXkgcFssFK',
				enabled: false,
				endpoint: undefined,
				integrationInfo: [],
				locationTimeoutMillisAndroid: 10000,
				region: 'eu',
				remoteControlDetection: false,
				scriptUrlPattern: undefined
			})
			.describe('FingerprintJS Pro configuration'),
		ftoAmountToShow: zWithTags(z.number().default(1).describe('FTO amount to show'), ['fto']),
		ftoPaymentTimeoutSeconds: zWithTags(z.number().default(900).describe('FTO payment timeout in seconds'), [
			'fto'
		]),
		ftoWaveInfo: zWithTags(
			z
				.array(
					z.object({
						amount: z.string().nullable(),
						order: z.string()
					})
				)
				.default([
					{ amount: '100', order: 'First row' },
					{ amount: '150', order: 'Second row' },
					{ amount: '', order: 'Third row' }
				])
				.describe(
					`Fan Token Offering wave cap amounts and order of rows. Empty amount means a wave with 'No Limit' cap`
				),
			['fto']
		),
		howDoIAdd$CHZLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/articles/4403721183121-Adding-CHZ-to-your-wallet-')
			.describe('How do I add $CHZ link'),
		howDoIGetFanTokensLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/articles/4403726960145-How-do-I-get-Fan-Tokens-')
			.describe('How do I get fan tokens link'),
		imageOptimizationProvider: z
			.string()
			.default('next')
			.describe('Provider for image optimization (next | cloudflare)'),
		imageOptimizationQuality: z.number().default(85).describe('Quality of the image optimization'),
		inHouseSSUReward: z
			.boolean()
			.default(true)
			.describe('whether to use in house SSU Reward or not. False will use GWEN service'),
		instagramLink: z.string().default('https://www.instagram.com/sociosdotcom/').describe('Instagram link'),
		interestsPromptEligibilityDate: z
			.string()
			.default('2024-06-28T13:09:00.000Z')
			.describe('Users registered after this date would be prompted to specify their interests'),
		iosChilizXAppLink: z
			.string()
			.default('https://apps.apple.com/fr/app/chiliz-exchange/id1493980225')
			.describe('iOS Chiliz X App link'),
		iosVersion: z.string().default('1.0.0').describe('iOS version'),
		isAccountManagementEnabled: z.boolean().default(true).describe('Is account management enabled'),
		isAdvancedTradingEnabled: z.boolean().default(true).describe('Is advanced trading enabled'),
		isAppStoreReviewEnabled: z.boolean().default(false).describe('Is mobile app stores review dialog enabled'),
		isATTPopupEnabled: z.boolean().default(false).describe('Is ATT popup enabled'),
		isBetaEnabled: z.boolean().default(false).describe('Is beta enabled'),
		isBrazeEnabled: z.boolean().default(false).describe('Enable the Braze customer engagement integration'),
		isBrowserSectionEnabled: z.boolean().default(false).describe('Is browser section enabled'),
		isCapsicumTopUpEnabled: z
			.boolean()
			.default(false)
			.describe('flag indicates if Top Up feature is enabled in Capsicum'),
		isCC2Enabled: z.boolean().default(true).describe('Is CC2 enabled'),
		isCheckinEnabled: z.boolean().default(false).describe('Is check in enabled'),
		isCheckInUploadCameraEnabled: z.boolean().default(false).describe('Is check in upload camera enabled'),
		isCheckInUploadGalleryEnabled: z.boolean().default(false).describe('Is check in upload gallery enabled'),
		isCheckInUploadImagesEnabled: z.boolean().default(false).describe('Is check in upload images enabled'),
		isCountryStepRegistrationStepEnabled: z
			.boolean()
			.default(true)
			.describe('Indicates whether to show the country step during registration'),
		isDigitalCollectiblesEnabled: z.boolean().default(true).describe('Is digital collectibles enabled'),
		isDigitalCollectiblesWithdrawalEnabled: z
			.boolean()
			.default(true)
			.describe('Is digital collectibles withdrawal enabled'),
		isExportWalletEnabled: z
			.boolean()
			.default(false)
			.describe('Are wallet exports available in the Account Management sections in Settings'),
		isExternalCurrencyInWalletEnabled: z
			.boolean()
			.default(false)
			.describe('Is external currency in wallet enabled'),
		isFanRewardEnabled: z.boolean().default(false).describe('Is fan reward enabled'),
		isFanTokenDirectPurchaseEnabled: z.boolean().default(false).describe('Is fan token direct purchase enabled'),
		isFavouriteTeamEnabled: z.boolean().default(true).describe('Is favourite team enabled'),
		isFavouriteTeamFeedFiltersEnabled: z
			.boolean()
			.default(false)
			.describe('Are favourite team feeed filters enabled'),
		isFavouriteTeamSelectionDisabled: z
			.boolean()
			.default(false)
			.describe('Determines whether a user is permitted access to the favourite team selection feature.'),
		isFollowedTeamSelectionDisabled: z
			.boolean()
			.default(false)
			.describe('Determines whether a user is permitted access to the follow team selection feature.'),
		isFreekickEnabled: z.boolean().default(false).describe('Is freekick enabled'),
		isFreemiumCheckInEnabled: z.boolean().default(false).describe('Is freemium check in enabled'),
		isFreemiumFreekickChallengeEnabled: z
			.boolean()
			.default(false)
			.describe('Is freemium freekick challenge enabled'),
		isFreemiumPredictionAndCheckInInformativeModalEnabled: z
			.boolean()
			.default(false)
			.describe('Is freemium prediction and check in informative modal enabled'),
		isFreemiumSwapAndPopEnabled: z.boolean().default(false).describe('Is freemium swap and pop enabled'),
		isFTOEnabled: zWithTags(z.boolean().default(true).describe('Is FTO enabled'), ['fto']),
		isFtoHomeSectionEnabled: z.boolean().default(true).describe('Is fan token offerings home carousel is enabled'),
		isGeestestDisabled: z.boolean().default(false).describe('Is Geestest disabled'),
		isGeetestDisabled: z.boolean().default(false).describe('Is Geetest disabled'),
		isGeetestDisabledCapsicum: z.boolean().default(false).describe('Is Geetest disabled (Capsicum only)'),
		isGoogleConsentDialogEnabled: z.boolean().default(false).describe('Is google consent dialog enabled'),
		isGreetingsScreenEnabled: z
			.boolean()
			.default(false)
			.describe('Indicates whether the greetings screen should be shown upon user registration'),
		isIAMTopupRequestEnabled: z.boolean().default(true).describe('Is IAM topup request enabled'),
		isImageOptimizationEnabled: z.boolean().default(false).describe('Is image optimization enabled'),
		isInterestsScreenEnabled: z.boolean().default(false).describe('Is interets screen enabled'),
		isInternationalWalletV2Enabled: z.boolean().default(false).describe('Is international wallet V2 enabled'),
		isInternationalWebWalletV2Enabled: z
			.boolean()
			.default(false)
			.describe('Is international web wallet V2 enabled'),
		isInUseEnabled: z.boolean().default(false).describe('Is "In use" enabled'),
		isLockerRoomEnabled: z.boolean().default(false).describe('Is locker room enabled'),
		isLockerRoomParticipationEnabled: z.boolean().default(false).describe('Is locker room participation enabled'),
		isLockerRoomTokensNeededLabelEnabled: z
			.boolean()
			.default(true)
			.describe('Is locker room tokens needed label feature enabled on locker room feed posts'),
		isLockerRoomUnlockingEnabled: z.boolean().default(false).describe('Is locker room unlocking enabled'),
		isMaintenanceEnabled: z.boolean().default(false).describe('Is maintenance enabled'),
		isMarketingBannerEnabled: z.boolean().default(false).describe('Is marketing banner enabled on todo'),
		isMarketingEmailConsentVisible: z
			.boolean()
			.default(false)
			.describe('Is marketing email consent section visible'),
		isMarketplaceEnabled: z.boolean().default(false).describe('Is marketplace enabled'),
		isMarketplaceFanTokenBuyEnabled: z.boolean().default(false).describe('Is Buy Fan Tokens in Capsicum enabled'),
		isMarketplaceFanTokenSellEnabled: z.boolean().default(false).describe('Is marketplace fan token sell enabled'),
		isNCWalletNFTsEnabled: z.boolean().default(false).describe('Is nc wallet nfts section enabled'),
		isNCWCreationFlowEnabled: z.boolean().default(false).describe('Is ncw creation flow enabled'),
		isNCWNftWithdrawEnabled: z.boolean().default(false).describe('Is ncw NFT withdraw enabled'),
		isNewFeedTeamSelectionEnabled: z.boolean().default(false).describe('Is new feed team selection enabled'),
		isNewOnboardingEnabled: z.boolean().default(false).describe('Is new onboarding enabled'),
		isOnRamperDisclaimerEnabled: z.boolean().default(false).describe('Is On Ramper disclaimer enabled'),
		isPartnerGamesFeedEnabled: z.boolean().default(false).describe('Is partner games feed enabled'),
		isPartnerTeamFeedEnabled: z.boolean().default(false).describe('Is partner team feed enabled'),
		isPasswordSettingEnabled: z.boolean().default(true).describe('Is password setting enabled'),
		isPoblanoEnabled: zWithTags(z.boolean().default(false).describe('Is poblano enabled'), ['poblano']),
		isPoblanoFanTokenDirectPurchaseEnabled: z
			.boolean()
			.default(false)
			.describe('Is Poblano FanToken direct purchase enabled'),
		isPoblanoPartnerPageEnabled: z.boolean().default(false).describe('Is Poblano partner page enabled'),
		isPollEnabled: z.boolean().default(false).describe('Is poll enabled'),
		isPOTMEnabled: z.boolean().default(false).describe('Is POTM enabled'),
		isPriceImpactEnabled: z
			.boolean()
			.default(false)
			.describe('Determines whether to show Price Impact info in Buy & Sell Flows'),
		isPushNotificationsEnabled: z.boolean().default(false).describe('Is push notifications enabled'),
		isRegistrationByEmailEnabled: z.boolean().default(false).describe('Is registration by email enabled'),
		isReverseAuctionEnabled: ZodBuilderUtils.fromJsonStringSchema(
			z.object({ mobile: z.boolean(), web: z.boolean() }),
			{
				mobile: false,
				web: false
			}
		).describe('Is reverse auction enabled'),
		isRewardPointAuctionsEnabled: z.boolean().default(false).describe('Is Rewards Points auctions enabled'),
		isRewardPointsMultiplierEnabled: z
			.boolean()
			.default(false)
			.describe('Banned countries for reward points multiplier'),
		isSideBarWalletEnabled: z.boolean().default(false).describe('Is sidebar wallet enabled'),
		isSocialFeedEnabled: z.boolean().default(false).describe('Is social feed enabled'),
		isSociosCollectiblesEnabled: z.boolean().default(false).describe('Is Socios collectibles enabled'),
		isSportEventsEnabled: z.boolean().default(false).describe('Is sport events enabled'),
		isStakeAllFanTokensEnabled: z.boolean().default(false).describe('Enables entry point for stake all fan tokens'),
		isSuggestedTasksFeedEnabled: z.boolean().default(false).describe('Is suggested tasks feed enabled'),
		isSuggestedTasksFeedFiltersEnabled: z
			.boolean()
			.default(false)
			.describe('Are suggested tasks feed filters enabled'),
		isSurveyV3Enabled: z.boolean().default(false).describe('Is survey V3 enabled'),
		isSwapAndPopEnabled: z.boolean().default(false).describe('Is swap and pop enabled'),
		isTeamSelectorInitFlowDisabled: z
			.boolean()
			.default(false)
			.describe(
				'Determines whether to hide the Choose Your Team or Fav. Team Selector during registration and/or Login flow respectively.'
			),
		isThemeSelectionEnabled: z
			.boolean()
			.default(false)
			.describe('whether switching between light & dark mode is enabled in the app'),
		isThirdPartyTopUpEnabled: z.boolean().default(false).describe('Is Third party topup enabled'),
		isTokenHuntEnabled: z.boolean().default(false).describe('Is Token Hunt enabled'),
		isTokenHuntSpecialEventsEnabled: z.boolean().default(false).describe('Is Token Hunt Special Events enabled'),
		isTokenHuntTemporaryViewOnWebEnabled: z.boolean().default(false).describe('Is T.Hunt Temp View on Web enabled'),
		isTopupEnabled: z.boolean().default(true).describe('Is topup enabled'),
		isTradeChartEnabled: z.boolean().default(true).describe('Is trade chart enabled'),
		isTransactionBuilderEnabled: z.boolean().default(false).describe('Is "internal"transaction builder enabled'),
		isTransactionHistoryEnabled: z.boolean().default(true).describe('Is transaction history enabled'),
		isTransactionSponsored: z
			.object({
				fto: z.boolean(),
				ftoClaim: z.boolean(),
				stakeAllFt: z.boolean(),
				survey: z.boolean(),
				worldChampionshipPledge: z.boolean()
			})
			.default({
				fto: false,
				ftoClaim: false,
				stakeAllFt: false,
				survey: false,
				worldChampionshipPledge: false
			})
			.describe(`Is a specific type of transaction's gas fees sponsored through the use of a paymaster`),
		isTurkeyMobileBlockEnabled: z.boolean().default(false).describe('Is Turkey mobile block enabled'),
		isTypeformIntegrationEnabled: z.boolean().default(false).describe('Is typeform integration enabled'),
		isTypeformStakedParticipationEnabled: z
			.boolean()
			.default(false)
			.describe('is typeform staked participation enabled'),
		isUsaFtoEnabled: z.boolean().default(false).describe('Is USA FTO enabled'),
		isUsaTopupEnabled: z.boolean().default(false).describe('Is USA topup enabled'),
		isUsaWalletV2Enabled: z.boolean().default(false).describe('Is USA wallet V2 enabled'),
		isUsaWebWalletV2Enabled: z.boolean().default(false).describe('Is USA web wallet V2 enabled'),
		isUsaWithdrawalEnabled: z.boolean().default(false).describe('Is USA withdrawal enabled'),
		isUsernameStepRegistrationEnabled: z
			.boolean()
			.default(true)
			.describe('Indicates whether to show the username step during registration'),
		isUsernameSuggestionsEnabled: z.boolean().default(true).describe('Is username suggestions enabled'),
		isVoucherEnabled: z.boolean().default(false).describe('Is voucher enabled'),
		isWalletConnectEnabled: z.boolean().default(false).describe('Is Wallet Connect feature enabled'),
		isWalletEnabled: z.boolean().default(false).describe('Is wallet enabled'),
		isWalletFavouriteTeamFTSectionEnabled: z
			.boolean()
			.default(false)
			.describe('Indicates whether Favourite Fan Tokens section within the Wallet page is enabled'),
		isWalletTopMovingFTSectionEnabled: z
			.boolean()
			.default(false)
			.describe('Indicates whether Top Moving Fan Tokens section within the Wallet page is enabled'),
		isWcPledgeEnabled: zWithTags(
			z.boolean().default(false).describe('Enables entry point for world championship pledge'),
			['wc-pledge']
		),
		isWcPledgePhase2Enabled: zWithTags(
			z.boolean().default(false).describe('Enables the phase 2 of the locker room internationals feature'),
			['wc-pledge']
		),
		isWebAchievementsEnabled: z.boolean().default(false).describe('Is web achievements enabled'),
		isWebCheckInEnabled: z.boolean().default(false).nullable().describe('Is web check in enabled'),
		isWebFanRewardEnabled: z.boolean().default(false).describe('Is web fan reward enabled'),
		isWebRewardsEnabled: z.boolean().default(false).describe('Is web rewards enabled'),
		isWebSportEventsEnabled: z.boolean().default(false).describe('Is web sport events enabled'),
		IsWhatsAppCodesEnabled: z
			.boolean()
			.default(false)
			.describe('Indicates whether users can send login code via WhatsApp'),
		isWithdrawalEnabled: z.boolean().default(false).describe('Is withdrawal enabled'),
		linkedinLink: z.string().default('https://www.linkedin.com/company/socios-com/').describe('Linkedin link'),
		lockerRoomInternationalsConfig: zWithTags(
			z
				.object({
					errors: z
						.array(
							z.object({
								minimumCHZAmountToStake: z.number().min(0)
							})
						)
						.optional(),
					leaderboardMaxTeams: z.number().int().min(1),
					maxTeamsPerPledge: z.number().min(1),
					minimumCHZAmountToStake: z.number(),
					showWCLockerRoomLearboardTab: z.boolean(),
					termsUrl: z.string()
				})
				.default({
					errors: [],
					leaderboardMaxTeams: 10,
					maxTeamsPerPledge: 15,
					minimumCHZAmountToStake: 100,
					showWCLockerRoomLearboardTab: false,
					termsUrl:
						'https://www.socios.com/legal-hub/socios-platform/terms/locker-room-internationals-terms/en/'
				})
				.describe('Locker Room Internationals exposed config'),
			['wc-pledge']
		),
		machineTestingMetadata: z
			.string()
			.describe('Credential of users to bypass dfns calls')
			.nullable()
			.default(null),
		maxBuySellDecimalInput: z
			.number()
			.int()
			.default(4)
			.describe('Maximum number of decimal detail allowed in buy & sell flow'),
		mediumLink: z.string().default('https://medium.com/tag/nft').describe('Medium link'),
		mimeTypes: z
			.object({
				APPLICATION: z.array(z.string()).optional(),
				AUDIO: z.array(z.string()).optional(),
				IMAGE: z.array(z.string()).optional(),
				VIDEO: z.array(z.string()).optional()
			})
			.default({
				APPLICATION: [],
				AUDIO: [],
				IMAGE: [],
				VIDEO: []
			})
			.describe('Mime types'),
		minDistanceBetweenPOIs: z.number().default(35).describe('Minimum distance between POIs'),
		minimumAllowedAndroidVersion: z.string().default('v2.63.0').describe('Minimum allowed version (Android only)'),
		minimumAllowedIOSVersion: z.string().default('v2.63.0').describe('Minimum allowed version (iOS only)'),
		minimumAllowedVersionCapsicum: z.string().default('0.0.0').describe('Minimum allowed version (Capsicum only)'),
		mwsBannedCountries: z.array(z.string()).default([]).describe('MWS banned countries'),
		mwsTermsAndConditionsLink: z
			.string()
			.default('https://mobile-app-content.socios.com/en_gb/terms-conditions-mws/')
			.describe('MWS Terms and Conditions link'),
		partnerPageOrder: z.array(z.string()).default([]).describe('Partner page order'),
		playStoreUrlCapsicum: z
			.string()
			.default('https://play.google.com/store/apps/details?id=com.socios')
			.describe('Play store url'),
		poblanoReleaseDate: z.string().default('2024-08-13T11:20:00.000Z').describe('Poblano release date'),
		POImetresAway: z.number().default(-220).describe('Distance in metres from POI'),
		POIpositionFromUser: z.number().default(250).describe('Distance from the user in metres from POI'),
		preventBuyFanTokensModal: z.boolean().default(false).describe('Prevent Buy Fan Tokens Modal to open'),
		privacyPolicyLink: z
			.string()
			.default('https://mobile-app-content.socios.com/en_gb/privacy-policy/')
			.describe('Privacy policy link'),
		pushNotificationsActionsPromptMinutesDelay: z
			.number()
			.default(15)
			.describe('Push notifications prompt minutes delay after user actions'),
		pushNotificationsPromptMinutesDelay: z.number().default(15).describe('Push notifications prompt minutes delay'),
		pwaForceInstall: PwaForceInstallSchema.describe('PWA force install'),
		reactQueryCacheConfig: z
			.object({
				enabledKeys: z
					.array(z.string())
					.default(['user', 'me-avatar'])
					.describe('React query cache enabled keys, can be a regex pattern'),
				isEnabled: z.boolean().default(true).describe('Is react query cache enabled'),
				version: z.string().default('2').describe('React query cache version')
			})
			.default({
				enabledKeys: ['user', 'me-avatar'],
				isEnabled: true,
				version: '1'
			})
			.describe('React query cache config'),
		redditLinkChz: z.string().default('https://www.reddit.com/r/chiliZ').describe('Reddit Chz link'),
		repeatFtoDisclaimer: zWithTags(z.boolean().default(false).describe('Repeat Fan Token Offering disclaimer'), [
			'fto'
		]),
		responsibleTradingLink: z
			.string()
			.default('https://www.socios.com/responsible-operation-responsible-trading/')
			.describe('Responsible trading link'),
		rewardsLinks: ZodBuilderUtils.fromJsonStringSchema(
			z.object({ auctionsFaq: z.string(), fanshopFaq: z.string() }),
			{
				auctionsFaq: 'https://www.socios.com/fan-rewards-faq/',
				fanshopFaq: 'https://www.socios.com/fan-rewards-faq/'
			}
		).describe('Special event leaderboard'),
		rpcUrl: z
			.string()
			.default('https://chiliz-rpc.publicnode.com')
			.describe(
				'RPC url. Lists can be found in https://docs.chiliz.com/develop/basics/connect-to-chiliz-chain/connect-using-rpc#connecting-to-mainnet)'
			),
		sellFlowExpirationPeriod: z
			.string()
			.default('PT3M')
			.describe('Period in mins that user have to finish the FanToken Sell journey'),
		sellSlippagePercentage: z.string().default('10').describe('Percentage of slippage for the sell flow'),
		sentrySampleRates: z
			.object({
				errors: z
					.array(
						z.object({
							match: z.string(),
							rate: z.number().min(0).max(1)
						})
					)
					.optional(),
				profiles: z.number(),
				replaysOnError: z.number(),
				replaysSession: z.number(),
				traces: z.number(),
				wsConnexionError: z.number()
			})
			.default({
				errors: [],
				profiles: 0.4,
				replaysOnError: 0.2,
				replaysSession: 0,
				traces: 0.2,
				wsConnexionError: 0
			})
			.describe('Sentry sample rates'),
		showAllTimePeriodOnCharts: z.boolean().default(false).describe('Show and select all time period on charts'),
		showAvailableFanZoneActivitiesIndicator: z
			.boolean()
			.default(false)
			.describe('Enable indicator for available fan zone activities (red dot)'),
		showCrossSellBanner: z.boolean().default(false).describe('Show cross sell banner'),
		showInternalPartners: z.boolean().default(false).describe('Show internal partners'),
		showRequirementsPage: z.boolean().default(false).describe('Show new personal verification page'),
		showTokenNeededLink: z.boolean().default(true).describe('Show Buy Fan Tokens link'),
		showVatSectionForFTO: zWithTags(
			z.boolean().default(false).describe('Show VAT section on the FTO review page'),
			['fto']
		),
		sociosWalletLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/articles/28202273834001-Socios-com-Wallet')
			.describe('Socios wallet link'),
		sportEventsCutOffTimeInUTC: z
			.string()
			.default('August 17, 2022 05:30:00 UTC')
			.describe('Sport events cut off time'),
		stakeAndEarnLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/categories/32357384907409-Stake-Earn')
			.describe('Stake and earn link'),
		stakeLink: z.string().default('https://governance.chilizchain.com/staking').describe('Wallet Stake link'),
		stakePepperLink: z.string().default('https://peppercoin.com/pepper-inc').describe('Pepper Stake link'),
		stateAndZipAreNeededInCountries: zWithTags(
			z.array(z.string()).default(['USA', 'CAN']).describe('State and Zip required countries'),
			['poblano']
		),
		sumSubPolicyLink: z
			.string()
			.default('https://sumsub.com/privacy-notice-service/')
			.describe('Sumsub policy link'),
		supportedSportsMatches: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Supported sports matches'
		),
		supportedSportsMatches_V2: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Supported sports matches (v2)'
		),
		supportedSportsPartner: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Supported sports partner'
		),
		supportedSportsPartner_V2: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Supported sports partner (v2)'
		),
		tabNavigationItems: z
			.array(
				z.object({
					name: z.nativeEnum(TabNavigationItemName),
					order: z.number()
				})
			)
			.min(4)
			.max(5)
			.default([
				{ name: TabNavigationItemName.Wallet, order: 0 },
				{ name: TabNavigationItemName.Charts, order: 1 },
				{ name: TabNavigationItemName.Market, order: 2 },
				{ name: TabNavigationItemName.Browser, order: 3 },
				{ name: TabNavigationItemName.Rewards, order: 4 }
			])
			.describe(`Tab navigation items, valid names: ${Object.values(TabNavigationItemName).join(', ')}`),
		// DO NOT REMOVE: required for configuration migration
		tags: TagSchema,
		telegramLinkChz: z.string().default('https://t.me/chiliztrade').describe('Telegram Chz link'),
		topUpProcessExpirationPeriod: z
			.string()
			.default('PT15M')
			.describe('Period in mins that user have to finish the top up journey'),
		transactionHistoryFAQslinkNetworkFeeTooltip: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/articles/4419264065297-Are-there-any-fees-on-Socios-com')
			.describe('Network Fee FAQs link on transaction history cards'),
		travelRuleDate: z.string().default('2025-01-01').describe('Travel rule release date'),
		twitterLink: z.string().default('https://twitter.com/socios').describe('Twitter link'),
		ukApprovedByFooter: z
			.object({
				auction: z.object({ date: z.string(), visible: z.boolean() }),
				buy: z.object({ date: z.string(), visible: z.boolean() }),
				charts: z.object({ date: z.string(), visible: z.boolean() }),
				fanTokenWallet: z.object({ date: z.string(), visible: z.boolean() }),
				fto: z.object({ date: z.string(), visible: z.boolean() }),
				home: z.object({ date: z.string(), visible: z.boolean() }),
				lockerRoom: z.object({ date: z.string(), visible: z.boolean() }),
				market: z.object({ date: z.string(), visible: z.boolean() }),
				nfts: z.object({ date: z.string(), visible: z.boolean() }),
				partner: z.object({ date: z.string(), visible: z.boolean() }),
				pay: z.object({ date: z.string(), visible: z.boolean() }),
				poll: z.object({ date: z.string(), visible: z.boolean() }),
				rewards: z.object({ date: z.string(), visible: z.boolean() }),
				rewardsAuctionProduct: z.object({ date: z.string(), visible: z.boolean() }),
				rewardsProduct: z.object({ date: z.string(), visible: z.boolean() }),
				sell: z.object({ date: z.string(), visible: z.boolean() }),
				tokenHunt: z.object({ date: z.string(), visible: z.boolean() }),
				topup: z.object({ date: z.string(), visible: z.boolean() }),
				tradeChart: z.object({ date: z.string(), visible: z.boolean() }),
				tradeMarket: z.object({ date: z.string(), visible: z.boolean() }),
				wallet: z.object({ date: z.string(), visible: z.boolean() }),
				wcPledge: z.object({ date: z.string(), visible: z.boolean() })
			})
			.default({
				auction: { date: '20/02/2023', visible: false },
				buy: { date: '20/01/2023', visible: false },
				charts: { date: '07/02/2025', visible: false },
				fanTokenWallet: { date: '10/09/2099', visible: false },
				fto: { date: '06/03/2025', visible: false },
				home: { date: '20/03/2023', visible: false },
				lockerRoom: { date: '29/07/2024', visible: false },
				market: { date: '07/02/2025', visible: false },
				nfts: { date: '10/09/2099', visible: false },
				partner: { date: '20/05/2023', visible: false },
				pay: { date: '16/01/2024', visible: false },
				poll: { date: '16/01/2024', visible: false },
				rewards: { date: '20/09/2023', visible: false },
				rewardsAuctionProduct: { date: '20/09/2023', visible: false },
				rewardsProduct: { date: '20/09/2023', visible: false },
				sell: { date: '20/07/2023', visible: false },
				tokenHunt: { date: '22/04/2024', visible: false },
				topup: { date: '20/09/2023', visible: false },
				tradeChart: { date: '07/02/2025', visible: false },
				tradeMarket: { date: '07/02/2025', visible: false },
				wallet: { date: '20/11/2023', visible: false },
				wcPledge: { date: '07/02/2025', visible: false }
			})
			.describe('UK approved by footer'),
		ukInvestmentRisksEnabled: z
			.boolean()
			.default(false)
			.describe('Indicates whether the investment risks are enabled for UK users'),
		ukRiskLink: z.string().default('https://www.twitch.tv').describe('UK risk link'),
		ukRiskLinkVisibility: z
			.object({
				partner: z.boolean(),
				wallet: z.boolean()
			})
			.default({
				partner: false,
				wallet: false
			})
			.describe('UK risk link visibility'),
		ukRiskWarning: z
			.object({
				auction: z.boolean(),
				buy: z.boolean(),
				charts: z.boolean(),
				fanTokenWallet: z.boolean(),
				fto: z.boolean(),
				home: z.boolean(),
				lockerRoom: z.boolean(),
				market: z.boolean(),
				nfts: z.boolean(),
				partner: z.boolean(),
				pay: z.boolean(),
				poll: z.boolean(),
				rewards: z.boolean(),
				rewardsAuctionProduct: z.boolean(),
				rewardsProduct: z.boolean(),
				sell: z.boolean(),
				tokenHunt: z.boolean(),
				topup: z.boolean(),
				trade: z.boolean(),
				wallet: z.boolean(),
				wcPledge: z.boolean()
			})
			.default({
				auction: false,
				buy: false,
				charts: false,
				fanTokenWallet: false,
				fto: false,
				home: false,
				lockerRoom: false,
				market: false,
				nfts: false,
				partner: false,
				pay: false,
				poll: false,
				rewards: false,
				rewardsAuctionProduct: false,
				rewardsProduct: false,
				sell: false,
				tokenHunt: false,
				topup: false,
				trade: false,
				wallet: false,
				wcPledge: false
			})
			.describe('UK risk warnings'),
		usaAppLaunchVersion: z.string().default('v0.0.1').describe('USA app launch version'),
		usaHiddenMilestones: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Hidden milestones for USA'
		),
		useInAppFlowForFTO: zWithTags(z.boolean().default(false).describe('Is in app FTO flow enabled'), ['fto']),
		userGuidelinesLink: z
			.string()
			.default('https://socios.zendesk.com/hc/fr/categories/6928967659793-Premier-pas')
			.describe('User guidelines link'),
		v2FFTMigrationFAQExternalLink: z
			.string()
			.default('http://example.com')
			.describe('V2 Fan Token Migration FAQ External Link'),
		v2SendWarningModal: z
			.boolean()
			.default(false)
			.describe(
				'Determines whether to show the V2 FT Supported Destinations Warning Modal when attempting to send V2 FTs'
			),
		v2SendWarningModalLink: z
			.string()
			.default(
				'https://socios.zendesk.com/hc/en-001/articles/40604718393233-Where-can-I-trade-and-store-my-V2-Fan-Tokens'
			)
			.describe('Link for the page listing all the exchanges and wallets that support v2 FTs.'),
		wcPledgeFaqsKeys: z
			.array(z.string())
			.default([
				'whatIsLockerRoomFeature',
				'whatIsLockerRoomCampaign',
				'whenDoesItStartEnd',
				'minimumStakeAmount',
				'multipleCampaigns',
				'favoriteTeamNotAvailable',
				'whatHappensToStakedChz',
				'transferStakedChz',
				'whatIsChzBonus',
				'howIsChzBonusCalculated',
				'howToUnstake',
				'campaignEnds',
				'whatIsOfferingStage',
				'canIUnstakeAfterTokenPrice',
				'whatDoIGetEarlyInterestStage',
				'forgetToUnstake',
				'whenWillIKnowTeamJoins',
				'areThereFeesCharged'
			])
			.describe('World Championship Pledge enabled FAQs keys'),
		webBlockedCountries: z.array(z.string()).default([]).describe('Blocked countries for web'),
		weiboLink: z.string().default('https://weibo.com/sociosdotcom?is_all=1').describe('Weibo link'),
		welcomeScreenModalRegulations: ZodBuilderUtils.fromJsonStringSchema(z.array(z.string()), []).describe(
			'Welcome screen modal regulations'
		),
		whatAreFanTokensLink: z
			.string()
			.default('https://app.socios.com/support#:~:text=What%20are%20Fan%20Tokens%3F')
			.describe('What are fan tokens link'),
		whatIsCHZLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/articles/360003217678-What-is-CHZ-')
			.describe('What is CHZ link'),
		withdrawalsAndTradingLink: z
			.string()
			.default('https://socios.zendesk.com/hc/en-001/categories/360003512397-Withdrawals-Trading')
			.describe('Withdrawals and trading link'),
		xLink: z.string().default('https://twitter.com/socios').describe('X link'),
		xLinkChz: z.string().default('https://www.x.com/chiliz').describe('X Chz link'),
		youtubeLink: z
			.string()
			.default('https://www.youtube.com/channel/UCzglT2jQzKy7z9hNFXN8geg')
			.describe('Youtube link')
	})
	.passthrough()
	.readonly();

export const defaultSchemaValues = SettingsSchema.parse({});

type ConfigSchema = z.infer<typeof SettingsSchema>;

type Literals = keyof {
	[K in keyof ConfigSchema as unknown extends ConfigSchema[K] ? never : K]: ConfigSchema[K];
};

export type SettingsSchemaType = Pick<ConfigSchema, Literals>;

export default SettingsSchema;
